// Keep this file in cross-portal parity; the matching client/superadmin copies are protected by
// backend/tests/presentationOwnershipHardening.test.js.
import React, { useMemo } from "react";
import facebookLogo from "../assets/facebook.jpg";
import instagramLogo from "../assets/instagram.jpg";
import tiktokLogo from "../assets/tiktok.jpg";
import youtubeLogo from "../assets/youtube.jpg";
import googleLogo from "../assets/google.jpg";
import displayLogo from "../assets/display.jpg";
import videoLogo from "../assets/video.jpg";
import ctvLogo from "../assets/ctv.jpg";
import doohLogo from "../assets/dooh.gif";
import xLogo from "../assets/x.jpg";
import truthSocialLogo from "../assets/truth-social-logo.jpg";
import rumbleLogo from "../assets/rumble-logo.jpg";
import audioLogo from "../assets/audio.jpg";
import emailLogo from "../assets/email.jpg";
import mobileLogo from "../assets/mobile.jpg";
import ingameLogo from "../assets/ingame.jpg";
import "./InsightNarrativeRenderer.css";

const BULLET_PREFIX_RE =
  /^\s*(?:🟣|✅|☑️|☑|✔️|✔|✓|■|▪|●|◦|◉|◆|♦|▶️|▶|-|\*)\s+/;
const SECTION_ICON_PREFIX_RE =
  /^\s*(?:(?:[▶►▷▸](?:\uFE0F|\uFE0E)?)|📘|📸|🎵|🐦|🏙️|🏙|📺|🎬|💻|🎞️|🎞|📈|🎯|🧭|🚀|🔍|🧠|🔎)\s+/;
const PLATFORM_TOKEN_PATTERN =
  "(?:youtube|facebook|instagram|tiktok|shopify|linkedin(?:\\s+ads)?|google search|search|google analytics|ga4|site analytics|display|online video(?:\\s*\\(olv\\))?|olv|connected tv(?:\\s*\\(ctv\\))?|ctv|digital-out-of-home(?:\\s*\\(dooh\\))?|dooh|cinema(?:\\s*\\(theatrical\\))?|theatrical|x(?:\\s*\\(twitter\\))?|truth social|rumble|audio|email|mobile|video|in-game|ingame)";
const INLINE_PLATFORM_SPLIT_RE = new RegExp(
  `\\s+(?=(?:[-–—•*])\\s*(?:${PLATFORM_TOKEN_PATTERN})\\b)`,
  "gi"
);
const NUMBERED_ACTION_SPLIT_RE =
  /\s+(?=(?:\d+[.)])\s+(?:maintain|continue(?:\s+to)?|keep|expand|accelerate|prioritize|pause|scale(?:-up)?|shift|reallocate|test|use|deploy|move|reduce|optimize|hold|sustain|protect|lean)\b)/gi;
const LEADING_ACTION_RE =
  /^(?:\d+[.)]\s*)?(?:maintain|continue(?:\s+to)?|keep|expand|accelerate|prioritize|pause|scale(?:-up)?|shift|reallocate|test|use|deploy|move|reduce|optimize|hold|sustain|protect|lean)\b/i;
const STRUCTURED_SPACER_TOKEN = "__INSIGHT_NARRATIVE_SPACER__";
const STRUCTURED_DIVIDER_TOKEN = "__INSIGHT_NARRATIVE_DIVIDER__";

const repairMetricDecimalSpacing = (value) =>
  String(value || "")
    .replace(/(\$\d[\d,]*)\.\s+(\d{2})\b/g, "$1.$2")
    .replace(/\b(\d{1,3})\.\s+(\d{1,2})\s*%/g, "$1.$2%");

const splitLines = (raw) =>
  String(raw || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");

const splitSmartLines = (raw) =>
  repairMetricDecimalSpacing(raw)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\s+(?=(_{12,}|—{6,}|-{6,}))/g, "\n")
    .replace(INLINE_PLATFORM_SPLIT_RE, "\n")
    .replace(NUMBERED_ACTION_SPLIT_RE, "\n")
    .split("\n");

const normalizeWhitespace = (value) =>
  repairMetricDecimalSpacing(value)
    .replace(/\u200B|\u200C|\u200D|\uFEFF/g, "")
    .replace(/\s+/g, " ")
    .trim();

const normalizePlainText = (value) =>
  splitSmartLines(value)
    .map((line) => String(line || "").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const stripPrefixes = (line) =>
  String(line || "")
    .replace(BULLET_PREFIX_RE, "")
    .replace(SECTION_ICON_PREFIX_RE, "")
    .trim();

const stripEmojiLike = (value) => {
  const input = String(value || "");
  try {
    return input
      .replace(/\p{Extended_Pictographic}+/gu, "")
      .replace(/\uFE0F/gu, "")
      .replace(/\uFE0E/gu, "")
      .replace(/\u200D/gu, "")
      .replace(/[☑✔✓■▪●◦◉◆♦▶►▷▸]/g, "")
      .trim();
  } catch {
    return input
      .replace(
        /[🔍🟣✅▶️🧠📘📸🎵🐦🏙️🏙📺🎬💻🎞️🎞📈🎯🧭🚀🔎]/g,
        ""
      )
      .replace(/[☑✔✓■▪●◦◉◆♦▶►▷▸]/g, "")
      .trim();
  }
};

const isDividerLine = (line) => /^[\s_—-]{12,}$/.test(String(line || "").trim());

const looksLikeKeyValue = (line) => {
  const text = normalizeWhitespace(line);
  if (!text.includes(":")) return false;
  const idx = text.indexOf(":");
  const left = text.slice(0, idx).trim();
  const right = text.slice(idx + 1).trim();
  if (!left || !right) return false;
  if (text.length > 180 || left.length > 48) return false;
  return true;
};

const looksLikeSubheading = (line) => {
  const text = normalizeWhitespace(line);
  if (!text) return false;
  if (/^(insight|summary|takeaway|learnings)\s*:$/i.test(text)) return true;
  if (/:$/.test(text) && text.length <= 110) return true;
  return false;
};

const looksLikeSectionTitle = (line) => {
  const text = normalizeWhitespace(line);
  if (!text) return false;
  if (text.length > 72) return false;
  if (looksLikeKeyValue(text)) return false;
  const known =
    /^(campaign notes|campaign strategy|progress assessment|campaign synthesis|channel[-\s]?level read|strategic actions|strategic takeaway|phase update|recommended direction|campaign memory|memory shift|platform performance|current campaign state|benchmark framing|final summary|platform roles|optimization focus|high[-\s]?performing levers|cross-platform summary|cross-platform implications|shopify|linkedin(\s+ads)?|google search|search|google analytics|ga4|site analytics|facebook|instagram|tiktok|youtube|display|olv|online video|ctv|connected tv|dooh|cinema|theatrical|x(\s*\(twitter\))?|truth social|rumble|audio|email|mobile|video|ingame|in-game)$/i;
  if (known.test(text)) return true;
  return /^[A-Z0-9][A-Za-z0-9 &/()+–—'’-]*$/.test(text) && text.length <= 56;
};

const normalizeHeadingText = (value) => {
  const text = normalizeWhitespace(stripEmojiLike(value));
  if (/^cross[-\s]?platform implications$/i.test(text)) return "Campaign Synthesis";
  if (/^x\s*\(twitter\)$/i.test(text)) return "X (Twitter)";
  if (/^ctv$/i.test(text)) return "Connected TV (CTV)";
  if (/^olv$/i.test(text)) return "Online Video (OLV)";
  if (/^dooh$/i.test(text)) return "Digital-Out-of-Home (DOOH)";
  if (/^cinema$/i.test(text)) return "Cinema (Theatrical)";
  if (/^google search$/i.test(text)) return "Google Search";
  if (/^(ga4|google analytics|site analytics)$/i.test(text)) {
    return "GA4 / Site Analytics";
  }
  return text;
};

const normalizeHeadingKey = (value) =>
  normalizeWhitespace(normalizeHeadingText(value))
    .toLowerCase()
    .replace(/[’]/g, "'")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const isHiddenFrameworkHeading = (value) =>
  /^(?:what['’]?s working|what went wrong|what we['’]?ve learned|what needs improvement|what we['’]?re doing next|what we can do better|metric|interpretation|implication|client[-\s]?ready closing)$/i.test(
    normalizeWhitespace(stripEmojiLike(value)).replace(/\s*[:\-–—]+\s*$/g, "")
  );

const stripHiddenFrameworkLabel = (value) =>
  normalizeWhitespace(value).replace(
    /\b(?:metric|interpretation|implication)\s*:\s*/gi,
    ""
  );

const ORPHAN_LEADING_METRIC_TOKEN_RE =
  /^(\s*)(?:\d[\d,]*(?:\.\d+)?%?)\s*(?:[;:,]\s*)+/;
const ORPHAN_STANDALONE_METRIC_TOKEN_RE =
  /^\s*(?:\d[\d,]*(?:\.\d+)?%?)\s*(?:[;:,]|\.)?\s*$/;
const ORPHAN_CENTS_FRAGMENT_RE =
  /^(?:\.?00|\d{2})\s+(?:distributed|allocated|planned|defines|establishes|frames|gives|is\s+allocated|is\s+planned)\b/i;
const TRUNCATED_PERCENT_METRIC_RE =
  /\b(?:completion\s+rate|CTR|engagement\s+rate|video\s+view\s+rate|video\s+completion\s+rate)\s+(?:is|was|at|near|to|of)\s+\d{1,3}\.(?!\d)/i;
const TRUNCATED_MONEY_METRIC_RE =
  /\b(?:CPC|CPM|effective\s+CPM|spend|budget|investment|cost)\s+(?:is|was|at|near|to|of)\s+\$?\d[\d,]*\.(?!\d)/i;
const INTERNAL_SCAFFOLDING_RE =
  /\b(?:budget ownership|channel roles tied to budget ownership|current ranking can use volume|keep channel roles tied)\b/i;

const stripOrphanLeadingMetricToken = (value) =>
  normalizeWhitespace(value).replace(ORPHAN_LEADING_METRIC_TOKEN_RE, "$1");

const isOrphanMetricFragmentLine = (value) => {
  const text = normalizeWhitespace(value);
  if (!text) return false;
  if (ORPHAN_STANDALONE_METRIC_TOKEN_RE.test(text)) return true;
  if (ORPHAN_CENTS_FRAGMENT_RE.test(text)) return true;
  if (TRUNCATED_PERCENT_METRIC_RE.test(text)) return true;
  if (TRUNCATED_MONEY_METRIC_RE.test(text)) return true;
  if (INTERNAL_SCAFFOLDING_RE.test(text)) return true;

  const stripped = stripOrphanLeadingMetricToken(text);
  return stripped !== text && /^(?:and|with|or|but)\b/i.test(stripped);
};

const dedupeRepeatedHeadingBlocks = (blocks = []) => {
  const seen = new Set();

  return (Array.isArray(blocks) ? blocks : []).filter((block) => {
    if (block?.type !== "heading") return true;
    if (isHiddenFrameworkHeading(block.text)) return false;

    const key = normalizeHeadingKey(block.text);
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const PLATFORM_HERO_ASSETS = {
  facebook: { src: facebookLogo, alt: "Facebook logo" },
  instagram: { src: instagramLogo, alt: "Instagram logo" },
  tiktok: { src: tiktokLogo, alt: "TikTok logo" },
  youtube: { src: youtubeLogo, alt: "YouTube logo" },
  "google search": { src: googleLogo, alt: "Google Search logo" },
  search: { src: googleLogo, alt: "Google Search logo" },
  display: { src: displayLogo, alt: "Display logo" },
  "online video (olv)": { src: videoLogo, alt: "Online Video logo" },
  olv: { src: videoLogo, alt: "Online Video logo" },
  video: { src: videoLogo, alt: "Video logo" },
  "connected tv (ctv)": { src: ctvLogo, alt: "Connected TV logo" },
  ctv: { src: ctvLogo, alt: "Connected TV logo" },
  "digital-out-of-home (dooh)": { src: doohLogo, alt: "DOOH logo" },
  dooh: { src: doohLogo, alt: "DOOH logo" },
  "x (twitter)": { src: xLogo, alt: "X logo" },
  x: { src: xLogo, alt: "X logo" },
  "truth social": { src: truthSocialLogo, alt: "Truth Social logo" },
  rumble: { src: rumbleLogo, alt: "Rumble logo" },
  audio: { src: audioLogo, alt: "Audio logo" },
  email: { src: emailLogo, alt: "Email logo" },
  mobile: { src: mobileLogo, alt: "Mobile logo" },
  "in-game": { src: ingameLogo, alt: "In-Game logo" },
  ingame: { src: ingameLogo, alt: "In-Game logo" },
};

const getPlatformHeroAsset = (value) =>
  PLATFORM_HERO_ASSETS[normalizeWhitespace(normalizeHeadingText(value)).toLowerCase()] || null;

const PLATFORM_SECTION_MATCHERS = [
  { heading: "YouTube", patterns: [/\byoutube\b/i] },
  { heading: "Facebook", patterns: [/\bfacebook\b/i] },
  { heading: "Instagram", patterns: [/\binstagram\b/i] },
  { heading: "TikTok", patterns: [/\btiktok\b/i] },
  { heading: "Google Search", patterns: [/\bgoogle search\b/i, /\bsearch\b/i] },
  {
    heading: "GA4 / Site Analytics",
    patterns: [/\bgoogle analytics\b/i, /\bga4\b/i, /\bsite analytics\b/i],
  },
  { heading: "Display", patterns: [/\bdisplay\b/i] },
  { heading: "Online Video (OLV)", patterns: [/\bonline video(?:\s*\(olv\))?\b/i, /\bolv\b/i] },
  { heading: "Connected TV (CTV)", patterns: [/\bconnected tv(?:\s*\(ctv\))?\b/i, /\bctv\b/i] },
  {
    heading: "Digital-Out-of-Home (DOOH)",
    patterns: [/\bdigital-out-of-home(?:\s*\(dooh\))?\b/i, /\bdooh\b/i],
  },
  {
    heading: "Cinema (Theatrical)",
    patterns: [/\bcinema(?:\s*\(theatrical\))?\b/i, /\btheatrical\b/i],
  },
  { heading: "X (Twitter)", patterns: [/\bx\s*\(twitter\)\b/i, /\bx\b/i] },
  { heading: "Truth Social", patterns: [/\btruth social\b/i] },
  { heading: "Rumble", patterns: [/\brumble\b/i] },
  { heading: "Audio", patterns: [/\baudio\b/i] },
  { heading: "Email", patterns: [/\bemail\b/i] },
  { heading: "Mobile", patterns: [/\bmobile\b/i] },
  { heading: "Video", patterns: [/\bvideo\b/i] },
  { heading: "In-Game", patterns: [/\bin-game\b/i, /\bingame\b/i] },
];

const stripOrdinalLead = (value) =>
  String(value || "")
    .replace(/^\s*(?:\d+[.)]|[A-Z][.)]|[IVX]+\.)\s+/i, "")
    .trim();

const findPlatformSectionLead = (value, { allowActionLead = false } = {}) => {
  const text = normalizeWhitespace(stripEmojiLike(stripPrefixes(value)));
  if (!text) return null;

  const searchable = stripOrdinalLead(text);
  let winner = null;

  PLATFORM_SECTION_MATCHERS.forEach((matcher) => {
    matcher.patterns.forEach((pattern) => {
      const match = pattern.exec(searchable);
      if (!match) return;
      const candidate = {
        heading: matcher.heading,
        index: match.index,
        length: match[0].length,
      };
      if (
        !winner ||
        candidate.index < winner.index ||
        (candidate.index === winner.index && candidate.length > winner.length)
      ) {
        winner = candidate;
      }
    });
  });

  if (!winner) return null;
  if (winner.index === 0) return { heading: winner.heading, text };
  if (!allowActionLead) return null;

  const prefix = searchable.slice(0, winner.index).trim();
  if (!prefix || !LEADING_ACTION_RE.test(prefix)) return null;
  if (winner.index > 84) return null;

  return { heading: winner.heading, text };
};

const blocksFromNarrativeLine = (type, text, options = {}) => {
  const platformLead = findPlatformSectionLead(text, options);
  if (!platformLead) return null;
  return [
    { type: "heading", variant: "hero", text: platformLead.heading },
    { type, text: platformLead.text },
  ];
};

const parseTextToBlocks = (text) => {
  const normalized = normalizePlainText(text);
  if (!normalized) return [];

  return splitLines(normalized).flatMap((rawLine) => {
    const original = String(rawLine || "").trimEnd();
    const trimmed = original.trim();

    if (!trimmed) return [{ type: "spacer" }];
    if (trimmed === STRUCTURED_SPACER_TOKEN) return [{ type: "spacer" }];
    if (trimmed === STRUCTURED_DIVIDER_TOKEN) return [{ type: "divider" }];
    if (isDividerLine(trimmed)) return [{ type: "divider" }];

    const clean = normalizeWhitespace(stripEmojiLike(stripPrefixes(trimmed)));
    if (!clean) return [];
    if (isHiddenFrameworkHeading(clean)) return [];
    if (isOrphanMetricFragmentLine(clean)) return [];
    const displayClean = stripOrphanLeadingMetricToken(stripHiddenFrameworkLabel(clean));
    if (isOrphanMetricFragmentLine(displayClean)) return [];
    if (!displayClean) return [];

    if (BULLET_PREFIX_RE.test(trimmed)) {
      const platformBlocks = blocksFromNarrativeLine("bullet", displayClean, {
        allowActionLead: true,
      });
      if (platformBlocks) return platformBlocks;

      if (looksLikeKeyValue(displayClean)) {
        const idx = displayClean.indexOf(":");
        return [
          {
            type: "bullet",
            label: displayClean.slice(0, idx).trim(),
            value: displayClean.slice(idx + 1).trim(),
          },
        ];
      }
      return [{ type: "bullet", text: displayClean }];
    }

    if (SECTION_ICON_PREFIX_RE.test(trimmed) || looksLikeSectionTitle(displayClean)) {
      return [
        {
          type: "heading",
          variant: SECTION_ICON_PREFIX_RE.test(trimmed) ? "hero" : "section",
          text: normalizeHeadingText(displayClean),
        },
      ];
    }

    if (looksLikeSubheading(displayClean)) {
      return [{ type: "subheading", text: displayClean }];
    }

    const platformParagraphBlocks = blocksFromNarrativeLine("paragraph", displayClean, {
      allowActionLead: LEADING_ACTION_RE.test(stripOrdinalLead(displayClean)),
    });
    if (platformParagraphBlocks) return platformParagraphBlocks;

    if (looksLikeKeyValue(displayClean)) {
      const idx = displayClean.indexOf(":");
      return [
        {
          type: "key_value",
          label: displayClean.slice(0, idx).trim(),
          value: displayClean.slice(idx + 1).trim(),
        },
      ];
    }

    return [{ type: "paragraph", text: displayClean }];
  });
};

const normalizeStructuredContent = (structured, section = "campaign_notes") => {
  if (!structured) return { section, blocks: [] };
  if (typeof structured === "string") {
    return { section, blocks: dedupeRepeatedHeadingBlocks(parseTextToBlocks(structured)) };
  }

  const rawBlocks = Array.isArray(structured.blocks) ? structured.blocks : [];
  const serialized = rawBlocks
    .map((block) => {
      const type = normalizeWhitespace(block?.type).toLowerCase();
      if (type === "divider") return STRUCTURED_DIVIDER_TOKEN;
      if (type === "spacer") return STRUCTURED_SPACER_TOKEN;
      if (type === "raw_lines" || type === "raw_text" || type === "verbatim_lines") {
        return Array.isArray(block?.lines)
          ? block.lines.map((line) => String(line || "").trimEnd()).join("\n")
          : String(block?.text || "");
      }
      if (type === "heading") {
        const text = normalizeHeadingText(block?.text || block?.label || block?.value || "");
        if (!text) return "";
        return block?.variant === "hero" ? `🧠 ${text}` : text;
      }
      if (type === "subheading") return normalizeWhitespace(block?.text || block?.label || "");
      if (type === "bullet") {
        const label = normalizeWhitespace(block?.label);
        const value = normalizeWhitespace(block?.value);
        const text = normalizeWhitespace(block?.text);
        const body = label && value ? `${label}: ${value}` : text || value;
        return body ? `- ${body}` : "";
      }
      if (type === "key_value" || type === "keyvalue") {
        const label = normalizeWhitespace(block?.label);
        const value = normalizeWhitespace(block?.value ?? block?.text);
        return label && value ? `${label}: ${value}` : "";
      }
      return normalizeWhitespace(block?.text || block?.value || block?.label);
    })
    .join("\n");

  const blocks = dedupeRepeatedHeadingBlocks(parseTextToBlocks(serialized));

  return {
    section: structured.section || section,
    blocks,
  };
};

function renderBodyText(text) {
  return String(text || "");
}

export default function InsightNarrativeRenderer({
  value,
  structured,
  emptyText = "No content yet…",
  className = "",
}) {
  const normalized = useMemo(
    () => normalizeStructuredContent(structured || value),
    [structured, value]
  );

  const blocks = Array.isArray(normalized.blocks) ? normalized.blocks : [];

  if (!blocks.length) {
    return <div className={`insight-narrative__empty ${className}`.trim()}>{emptyText}</div>;
  }

  return (
    <div className={`insight-narrative ${className}`.trim()}>
      {blocks.map((block, index) => {
        const key = `${block.type || "block"}-${index}`;

        if (block.type === "spacer") {
          return <div key={key} className="insight-narrative__spacer" aria-hidden="true" />;
        }

        if (block.type === "divider") {
          return <div key={key} className="insight-narrative__divider" aria-hidden="true" />;
        }

        if (block.type === "heading") {
          const heroAsset = getPlatformHeroAsset(block.text);
          return (
            <div
              key={key}
              className={`insight-narrative__hero ${
                block.variant === "hero" ? "is-emphasis" : ""
              }`}
            >
              <div className="insight-narrative__hero-content">
                {heroAsset ? (
                  <span className="insight-narrative__hero-logo-shell" aria-hidden="true">
                    <img
                      className="insight-narrative__hero-logo"
                      src={heroAsset.src}
                      alt={heroAsset.alt}
                    />
                  </span>
                ) : null}
                <div className="insight-narrative__hero-title">{block.text}</div>
              </div>
            </div>
          );
        }

        if (block.type === "subheading") {
          return (
            <div key={key} className="insight-narrative__subheading">
              {block.text}
            </div>
          );
        }

        if (block.type === "key_value") {
          return (
            <div key={key} className="insight-narrative__row">
              <div className="insight-narrative__label">{block.label}</div>
              <div className="insight-narrative__body">{renderBodyText(block.value)}</div>
            </div>
          );
        }

        if (block.type === "bullet") {
          return (
            <div key={key} className="insight-narrative__bullet">
              <span className="insight-narrative__bullet-marker">•</span>
              <div className="insight-narrative__bullet-content">
                {block.label ? (
                  <div className="insight-narrative__label">{block.label}</div>
                ) : null}
                <div className="insight-narrative__body">
                  {renderBodyText(block.value || block.text)}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={key} className="insight-narrative__paragraph">
            {renderBodyText(block.text)}
          </div>
        );
      })}
    </div>
  );
}
